import { RendererFactory2, Renderer2 } from '@angular/core';
import { take } from 'rxjs/operators';
import { CaasContentService } from '../services/caas-content.service';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

export function loadScriptsFactory(
  caasContentService: CaasContentService,
  rendererFactory: RendererFactory2
): () => Promise<void> {
  return () =>
    new Promise<void>((resolve) => {
      if (typeof window === 'undefined') {
        resolve();
        return;
      }
      if (typeof window !== 'undefined') {
        if (!window.dataLayer) {
          window.dataLayer = [];
        }
      }
      caasContentService
        .getHeader()
        .pipe(take(1))
        .subscribe({
          next: async (scriptContent: string | string[] | null) => {
            const renderer: Renderer2 = rendererFactory.createRenderer(
              null,
              null
            );
            const loadPromises: Promise<void>[] = [];

            if (scriptContent) {
              const scripts = Array.isArray(scriptContent)
                ? scriptContent
                : [scriptContent];

              for (const script of scripts) {
                const promises = await insertScript(script, renderer);
                loadPromises.push(...promises);
              }
            }

            await Promise.all(loadPromises);
            resolve();
          },
          error: (err: unknown) => {
            console.error('❌ Error loading scripts from backend:', err);
            resolve();
          },
        });
    });
}

async function insertScript(
  scriptContent: string,
  renderer: Renderer2
): Promise<Promise<void>[]> {
  const loadPromises: Promise<void>[] = [];
  const trimmed = scriptContent.trim();

  if (
    trimmed.startsWith('<!DOCTYPE') ||
    trimmed.includes('<html') ||
    trimmed.includes('<head')
  ) {
    console.warn(
      '⛔ Skipping HTML content — not a valid script:',
      trimmed.slice(0, 100)
    );
    return loadPromises;
  }

  // Case 1: <script> HTML block
  if (trimmed.includes('<script')) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(trimmed, 'text/html');

    // <noscript> into <body>
    const noscriptElements = doc.getElementsByTagName('noscript');
    for (let i = 0; i < noscriptElements.length; i++) {
      const newNoscript = renderer.createElement('noscript');
      newNoscript.innerHTML = noscriptElements[i].innerHTML;
      renderer.insertBefore(
        document.body,
        newNoscript,
        document.body.firstChild
      );
    }

    // <script> into <head>
    const scriptElements = doc.getElementsByTagName('script');
    for (let i = 0; i < scriptElements.length; i++) {
      const original = scriptElements[i];
      const newScript = renderer.createElement('script') as HTMLScriptElement;
      newScript.type = 'text/javascript';

      for (let j = 0; j < original.attributes.length; j++) {
        const attr = original.attributes[j];
        newScript.setAttribute(attr.name, attr.value);
      }

      newScript.text = original.textContent ?? '';

      if (
        newScript.text.trim().startsWith('<!DOCTYPE') ||
        newScript.text.includes('<html')
      ) {
        console.warn('⛔ Skipping embedded HTML in <script> tag');
        continue;
      }

      renderer.appendChild(document.head, newScript);

      if (newScript.src) {
        newScript.async = true;
        const promise = new Promise<void>((resolve) => {
          newScript.addEventListener('load', () => resolve());
          newScript.addEventListener('error', () => {
            console.error('❌ Script failed to load:', newScript.src);
            newScript.remove();
            resolve();
          });
        });
        loadPromises.push(promise);
      }
    }

    return loadPromises;
  }

  // Case 2: external script URL
  if (trimmed.startsWith('http') || trimmed.startsWith('/')) {
    const isValid = await validateScriptUrl(trimmed);
    if (!isValid) {
      console.warn('❌ Skipping invalid or blocked script URL:', trimmed);
      return loadPromises;
    }

    const scriptEl = renderer.createElement('script') as HTMLScriptElement;
    scriptEl.type = 'text/javascript';
    scriptEl.src = trimmed;
    scriptEl.async = true;

    const promise = new Promise<void>((resolve) => {
      scriptEl.addEventListener('load', () => resolve());
      scriptEl.addEventListener('error', () => {
        scriptEl.remove();
        resolve();
      });
    });

    renderer.appendChild(document.head, scriptEl);
    loadPromises.push(promise);

    return loadPromises;
  }

  // Case 3: inline JS
  const cleaned = trimmed.replace(/<\/?script[^>]*>/gi, '').trim();

  if (
    cleaned.startsWith('<') || // starts with < is always invalid
    cleaned.includes('<noscript') ||
    cleaned.includes('<iframe') ||
    cleaned.includes('<html') ||
    cleaned.includes('<body')
  ) {
    console.warn(
      '⛔ Aborting inline script — likely HTML, not JS:',
      cleaned.slice(0, 100)
    );
    return loadPromises;
  }

  const inlineScript = renderer.createElement('script') as HTMLScriptElement;
  inlineScript.type = 'text/javascript';
  inlineScript.text = cleaned;

  try {
    console.log('🧪 Trying to inject:', JSON.stringify(cleaned.slice(0, 100)));
    renderer.appendChild(document.head, inlineScript);
    console.debug('📦 Injected inline script:', cleaned.slice(0, 100));
  } catch (err) {
    console.error(
      '💥 Failed to append inline script:',
      cleaned.slice(0, 100),
      err
    );
  }

  return loadPromises;
}

async function validateScriptUrl(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    const contentType = response.headers.get('Content-Type') ?? '';
    return (
      contentType.includes('javascript') || contentType.includes('text/plain')
    );
  } catch (err) {
    console.warn('⚠️ Could not validate script URL:', url, err);
    return false;
  }
}

export {};
